import { useEffect, useMemo, useState } from "react";
import { Download, Flame, Volume2, VolumeX } from "lucide-react";
import { FAMILIES, MUSCLE, type Family } from "../../shared/model";
import { Button, LinkButton, useToast } from "../ui/kit";
import { Shell } from "../ui/Shell";
import { Link, navigate } from "../app/router";
import { useSession } from "../app/session";
import * as store from "../app/store";
import { useStore } from "../app/store";
import { api, deriveKey } from "../app/api";
import { calendar, streak, totals } from "../app/stats";
import { addDays, dayKey, duration, parseDay } from "../app/clock";
import { useSyncStatus, syncNow } from "../app/sync";
import { FAMILY_META, describe } from "../puzzles/catalog";
import { RecoveryKeyCard } from "./Auth";
import { Pond } from "../ui/Pond";

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="tile card">
      <div className="tile-n">{value}</div>
      <div className="tile-l">{label}</div>
      {sub && <div className="muted small">{sub}</div>}
    </div>
  );
}

function Heat({ secs, goal }: { secs: number; goal: number }) {
  const lvl = secs <= 0 ? 0 : secs < goal * 0.5 ? 1 : secs < goal ? 2 : 3;
  return <span className={"heat l" + lvl} />;
}

export default function You() {
  const toast = useToast();
  const { user } = useSession();
  const day = dayKey();
  const data = useStore((d) => d);
  const t = useMemo(() => totals(data), [data]);
  const st = useMemo(() => streak(data, day), [data, day]);
  const weeks = useMemo(() => calendar(data, 14, day), [data, day]);
  const goal = data.settings.goalMinutes * 60;
  const maxFam = Math.max(1, ...FAMILIES.map((f) => t.byFamily[f]));
  const journal = useMemo(
    () => Object.values(data.progress).filter((p) => p.status === "solved").sort((a, b) => (b.solvedAt ?? 0) - (a.solvedAt ?? 0)),
    [data.progress]
  );

  useEffect(() => {
    document.title = "You · Ponder";
    if (location.hash === "#account") document.getElementById("account")?.scrollIntoView();
  }, []);

  const exportData = () => {
    const blob = new Blob([JSON.stringify({ exported: new Date().toISOString(), progress: data.progress, days: data.days, others: data.others, settings: data.settings }, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "ponder-progress.json";
    a.click();
  };

  return (
    <Shell>
      <div className="wrap you">
        <header className="page-head">
          <p className="kicker">Your mind, growing</p>
          <h1>{(user?.name || data.name) ? `${(user?.name || data.name).split(" ")[0]}'s thinking` : "Your thinking"}</h1>
        </header>

        <Pond solved={journal} koi={Math.min(4, Math.floor(st.best / 3))} />

        <div className="tiles">
          <Tile label="thinking so far" value={duration(t.seconds)} />
          <Tile label="days of practice" value={String(t.days)} sub="with a minute or more" />
          <Tile label="puzzles solved" value={String(t.solved)} />
          <Tile label="days in a row" value={String(st.current)} sub={`best ${st.best}`} />
        </div>

        <section className="card block">
          <h2>Three muscles</h2>
          <p className="muted small">Each puzzle family trains a different way of thinking. Balance is the point.</p>
          <div className="muscles">
            {FAMILIES.map((f: Family) => (
              <div key={f} className="muscle-row">
                <div className="muscle-name"><span className={"chip " + MUSCLE[f].toLowerCase()}>{MUSCLE[f]}</span> {FAMILY_META[f].name}</div>
                <div className="bar"><div className={"bar-fill " + MUSCLE[f].toLowerCase()} style={{ width: `${(t.byFamily[f] / maxFam) * 100}%` }} /></div>
                <div className="muscle-min">{duration(t.byFamily[f])}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="card block">
          <div className="row"><h2>Last 14 weeks</h2><span className="grow" /><span className="muted small"><Flame size={14} style={{ verticalAlign: "-2px" }} /> a full square means you reached your goal</span></div>
          <div className="heatmap" role="img" aria-label="Calendar of days with thinking">
            {weeks.map((col, i) => (
              <div key={i} className="heat-col">
                {col.map((c) => (
                  <span key={c.day} title={`${parseDay(c.day).toLocaleDateString()}: ${duration(c.secs)}`} className={c.future ? "heat-cell future" : "heat-cell"}>
                    {!c.future && <Heat secs={c.secs} goal={goal} />}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </section>

        <section className="card block" id="journal">
          <h2>Your insights</h2>
          <p className="muted small">Notes you wrote in your own words after solving. Only you can see them.</p>
          {journal.length === 0 ? (
            <p className="muted">Solve a puzzle and jot down what the trick was. It will live here.</p>
          ) : (
            <ul className="journal">
              {journal.map((p) => {
                const info = describe(p.id);
                if (!info) return null;
                return (
                  <li key={p.id}>
                    <Link to={"/p/" + p.id} className="j-title">{FAMILY_META[p.family].name} · {info.title}</Link>
                    <span className="muted small"> · {p.result} · {p.solvedAt ? new Date(p.solvedAt).toLocaleDateString() : ""}</span>
                    {p.note && <p className="j-note">“{p.note}”</p>}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="card block" id="settings">
          <h2>How you like it</h2>
          <div className="settings-grid">
            <div className="field">
              <span>Daily thinking goal</span>
              <div className="seg">
                {[5, 10, 15, 20, 30].map((m) => (
                  <button key={m} type="button" className={data.settings.goalMinutes === m ? "on" : ""} onClick={() => store.setSettings({ goalMinutes: m })}>{m} min</button>
                ))}
              </div>
            </div>
            <div className="field">
              <span>Look</span>
              <div className="seg">
                {(["auto", "light", "dark"] as const).map((m) => (
                  <button key={m} type="button" className={data.settings.theme === m ? "on" : ""} onClick={() => store.setSettings({ theme: m })}>{m === "auto" ? "Match device" : m === "light" ? "Daylight" : "Night pond"}</button>
                ))}
              </div>
            </div>
            <div className="field">
              <span>Sounds</span>
              <div className="seg">
                <button type="button" className={!data.settings.sound ? "on" : ""} onClick={() => store.setSettings({ sound: false })}><VolumeX size={15} /> Quiet</button>
                <button type="button" className={data.settings.sound ? "on" : ""} onClick={() => store.setSettings({ sound: true })}><Volume2 size={15} /> A soft chime</button>
              </div>
            </div>
          </div>
        </section>

        <section className="card block" id="install">
          <h2>Keep it close</h2>
          <p className="muted">
            Ponder works like an app. On an iPhone or iPad, tap the Share button, then <strong>Add to Home Screen</strong>. On Android or a computer,
            look for <strong>Install</strong> in your browser's menu. Once installed it opens full-screen and keeps working without a connection.
          </p>
        </section>

        <AccountSection onExport={exportData} toast={toast} />
      </div>
    </Shell>
  );
}

function AccountSection({ onExport, toast }: { onExport: () => void; toast: (m: string, k?: "info" | "error") => void }) {
  const { user, setUser, signOut } = useSession();
  const { state, lastOk } = useSyncStatus();
  const [name, setName] = useState(user?.name ?? "");
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [keyPw, setKeyPw] = useState("");
  const [newKey, setNewKey] = useState<string | null>(null);
  const [delPw, setDelPw] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => setName(user?.name ?? ""), [user?.name]);

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    try {
      await fn();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };

  if (!user) {
    return (
      <section className="card block" id="account">
        <h2>Take it with you</h2>
        <p className="muted">Your progress is saved on this device only. A free account syncs it between your phone, tablet and computer, and keeps it if you ever lose this device.</p>
        <div className="row-gap" style={{ marginTop: 14 }}>
          <LinkButton to="/signup" kind="primary">Create free account</LinkButton>
          <LinkButton to="/login">Sign in</LinkButton>
          <Button onClick={onExport}><Download size={16} /> Export my progress</Button>
        </div>
      </section>
    );
  }

  return (
    <section className="card block" id="account">
      <h2>Your account</h2>
      <p className="muted">
        {user.email} ·{" "}
        {state === "syncing" ? "syncing…" : state === "offline" ? "offline: everything is safe on this device and will sync later" : lastOk ? `synced ${new Date(lastOk).toLocaleTimeString()}` : "ready to sync"}
      </p>
      <div className="row-gap" style={{ margin: "14px 0" }}>
        <Button onClick={() => void syncNow().then((ok) => toast(ok ? "Everything is up to date." : "Couldn't reach the server just now.", ok ? "info" : "error"))}>Sync now</Button>
        <Button onClick={onExport}><Download size={16} /> Export my progress</Button>
        <Button onClick={() => signOut().then(() => navigate("/welcome"))}>Sign out</Button>
        <Button kind="ghost" onClick={() => setOpen(!open)}>{open ? "Hide account settings" : "Account settings"}</Button>
      </div>
      {open && (
        <div className="account-grid">
          <div className="sub-card">
            <h3>Name</h3>
            <label className="field"><span>Your name</span><input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} /></label>
            <Button kind="pond" disabled={!!busy || name.trim() === user.name || !name.trim()} onClick={() => run("name", async () => { const r = await api.updateProfile({ name }); setUser(r.user); store.setName(r.user.name); toast("Saved"); })}>Save name</Button>
          </div>
          <div className="sub-card">
            <h3>Password</h3>
            <label className="field"><span>Current password</span><input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} autoComplete="current-password" /></label>
            <label className="field"><span>New password</span><input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" minLength={8} /></label>
            <Button kind="pond" disabled={!!busy || !oldPw || newPw.length < 8} onClick={() => run("pw", async () => {
              await api.changePassword(await deriveKey(oldPw, user.email), await deriveKey(newPw, user.email));
              setOldPw(""); setNewPw(""); toast("Password changed. Other devices were signed out.");
            })}>Change password</Button>
          </div>
          <div className="sub-card">
            <h3>Recovery key</h3>
            {newKey ? (
              <RecoveryKeyCard recoveryKey={newKey} onDone={() => setNewKey(null)} doneLabel="Done" />
            ) : (
              <>
                <p className="muted small">Lost your recovery key? Make a new one (the old one stops working).</p>
                <label className="field"><span>Password</span><input type="password" value={keyPw} onChange={(e) => setKeyPw(e.target.value)} autoComplete="current-password" /></label>
                <Button disabled={!!busy || !keyPw} onClick={() => run("key", async () => { const r = await api.newRecoveryKey(await deriveKey(keyPw, user.email)); setKeyPw(""); setNewKey(r.recoveryKey); })}>Make a new key</Button>
              </>
            )}
          </div>
          <div className="sub-card danger">
            <h3>Delete account</h3>
            <p className="muted small">Removes your account and all your progress from our servers. There's no undo.</p>
            <label className="field"><span>Password to confirm</span><input type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} autoComplete="current-password" /></label>
            <Button kind="danger" disabled={!!busy || !delPw} onClick={() => {
              if (!window.confirm("Delete your account and all your progress forever?")) return;
              void run("delete", async () => {
                await api.deleteAccount(await deriveKey(delPw, user.email));
                store.wipeAll();
                setUser(null);
                navigate("/welcome");
              });
            }}>Delete my account</Button>
          </div>
        </div>
      )}
    </section>
  );
}

export { addDays };
